import { FC, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { batch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { renderToast } from 'components/CustomToast/CustomToast';
import DayViewNavigation from 'components/DayViewNavigation/DayViewNavigation';
import {
  FamilyTimeSlot,
  FamilyTimeSlotBlock,
  IResolvedAvailability,
} from 'interfaces/familyBookingTypes';
import { ITimeSlot } from 'interfaces/timeslotType';
import {
  cachePatientAvailability,
  invalidatePatientAvailability,
  mergeSlotsPerDate,
  popFamilyStep,
  resetSelectedPatientAtIndex,
  resetSelectedPatients,
  resetSelectedPatientsFromIndex,
  setReviewOpen,
  updateSelectedPatientAtIndex,
  updatedCurrentSelectedDate,
  updatedCurrentSelectedPatientIndex,
} from 'redux/familyBookingSlice';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import {
  IFamilyBookingAuthOptions,
  checkFamilyBookingTimeBookabilityAPI,
  deleteFamilyBookingReservedSlotsAPI,
  getSuitableServiceAPI,
  reserveFamilyBookingSlotAPI,
} from 'services/APIs';
import { getClinicAvailability } from 'services/APIs/getClinicAvailability';
import { concatFullName } from 'utils/common';
import {
  MINUTE_OF_A_BLOCK,
  convertTimeHHmmss,
} from 'utils/convertMinutesIntoHoursOfDay';
import { getFilterDates } from 'utils/dates';
import { getPatientKey } from 'utils/getPatientKey';
import { parseQueryUrl } from 'utils/parseQueryUrl';
import { loadOtpToken } from 'utils/storage';

import NavigationController from '../NavigationController/NavigationController';
import {
  expireFamilyBookingSession,
  isFamilyBookingSessionExpired,
} from '../utils/familyBookingSession';
import { isSlotTimeBookable } from '../utils/utils';
import AvailabilityProgress from './AvailabilityProgress/AvailabilityProgress';
import BackConfirmationModal from './BackConfirmationModal/BackConfirmationModal';
import NextAvailability from './NextAvailability/NextAvailability';
import PatientInfoSelection from './PatientInfoSelection/PatientInfoSelection';
import SlotChangeConfirmationModal from './SlotChangeConfirmationModal/SlotChangeConfirmationModal';
import SlotGrid from './SlotGrid/SlotGrid';
import {
  AVAILABILITY_ERROR_MESSAGE,
  DATE_FORMAT,
  LOADING_AVAILABILITY_MESSAGE,
  MISSING_SESSION_ERROR_MESSAGE,
  RELEASE_SLOT_ERROR_MESSAGE,
  RELEASE_SLOTS_ERROR_MESSAGE,
  RESERVE_SLOT_ERROR_MESSAGE,
  SLOT_COMMIT_BEAT_MS,
  SLOT_NOT_BOOKABLE_MESSAGE,
} from './constants';
import { IPendingSlotChange, ISlotIntent, SlotMutation } from './types';
import {
  adaptAvailability,
  isAvailabilityCurrent,
  isExamAndCleaningService,
  getServiceDuration,
  resolveSelectedDate,
  sortPractitionerSlots,
} from './utils/availability';
import { buildAppointmentFromSlot } from './utils/buildAppointmentFromSlot';

import styles from './SlotSelection.module.scss';

const getRequestErrorMessage = (requestFailure: any, fallback: string) => {
  if (requestFailure?.response?.status === 400) {
    return requestFailure.response.data?.message || fallback;
  }

  return fallback;
};

const SlotSelection: FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { codeUrl, serviceSlugNameUrl } = parseQueryUrl(location);

  const {
    familyBookingSlice: {
      selectedPatients,
      currentSelectedPatientIndex,
      familyBookingSessionId,
      cachedPatientAvailability,
      step1: { phoneNumber },
      step2: { contactPatient },
    },
    clinicDetailsSlice: {
      clinicData: { id: clinicId, services, timezone, slotInterval },
    },
    bookingFormSlice: { selectedLastInsurance },
    authSlice: { tempAccessToken },
  } = useAppSelector((state) => state);

  const currentPatient = selectedPatients[currentSelectedPatientIndex];
  const patientKey = currentPatient ? getPatientKey(currentPatient) : '';
  const insuranceId =
    selectedLastInsurance?.insuranceCarrierId ||
    contactPatient.insurance?.insuranceId;

  const [draftDates, setDraftDates] = useState<Record<string, string>>({});

  const selectedDate = resolveSelectedDate({
    draftDates,
    activePatientKey: patientKey,
    appointmentDate: currentPatient?.appointmentDate,
  });

  const heldGrid = cachedPatientAvailability[patientKey] || null;

  const isViewingSelectedAppointmentDate =
    currentPatient?.isSlotSelected &&
    currentPatient.appointmentDate === selectedDate;

  const isHeldGridForSelectedDate = isAvailabilityCurrent({
    availability: heldGrid,
    patientKey,
    date: selectedDate,
  });

  const cachedAvailability =
    isViewingSelectedAppointmentDate && isHeldGridForSelectedDate
      ? heldGrid
      : null;

  const [fetchedAvailability, setFetchedAvailability] =
    useState<IResolvedAvailability | null>(null);
  const [isFetchingAvailability, setIsFetchingAvailability] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const isCacheHit = !!cachedAvailability;

  const availability = cachedAvailability || fetchedAvailability;
  const isLoadingAvailability = isCacheHit ? false : isFetchingAvailability;
  const availabilityError = isCacheHit ? null : fetchError;

  const isExamCleaningService = isExamAndCleaningService({
    serviceId: currentPatient?.serviceId || '',
    services,
  });

  const [mutation, setMutation] = useState<SlotMutation>({ status: 'idle' });
  const [pendingSlotChange, setPendingSlotChange] =
    useState<IPendingSlotChange | null>(null);
  const [isReleasingDependents, setIsReleasingDependents] = useState(false);
  const [isBackConfirmOpen, setIsBackConfirmOpen] = useState(false);

  // Written before any setState so a second click in the same tick cannot slip
  // past a `mutation` flag that has not re-rendered yet.
  const inFlightIntentRef = useRef<ISlotIntent | null>(null);

  // A reservation resolving after the step unmounts has nowhere to commit, so
  // it hands the hold back instead of leaking it until the server TTL expires.
  const isUnmountedRef = useRef(false);

  const navigatedPatientKeysRef = useRef<Set<string>>(new Set());

  const isMutatingSlot = mutation.status !== 'idle';

  useEffect(
    () => () => {
      isUnmountedRef.current = true;
    },
    []
  );

  useEffect(() => {
    dispatch(updatedCurrentSelectedPatientIndex(0));

    return () => {
      dispatch(updatedCurrentSelectedPatientIndex(0));
      dispatch(updatedCurrentSelectedDate(''));
    };
  }, [dispatch]);

  useEffect(() => {
    if (isCacheHit) return;

    if (!currentPatient?.serviceId || !clinicId || !timezone) {
      setFetchedAvailability(null);
      setIsFetchingAvailability(false);

      return;
    }

    const requestServiceId = currentPatient.serviceId;
    const patientId = currentPatient.id;
    const patientDob = currentPatient.dob;
    const preferredDentistId = currentPatient.preferredDentistId;
    const source = axios.CancelToken.source();

    // getSuitableServiceAPI takes no cancel token, so each resumption after an await is guarded by hand.
    let isCurrentRequest = true;

    const loadAvailability = async () => {
      setIsFetchingAvailability(true);
      setFetchError(null);

      try {
        let slotQueryServiceId = requestServiceId;

        if (isExamCleaningService) {
          const { serviceId: suitableServiceId } = await getSuitableServiceAPI(
            {
              clinicId,
              serviceId: requestServiceId,
              appointmentDate: selectedDate,
              ...(patientId ? { patientId } : { patientDob }),
            },
            {
              otpToken: loadOtpToken(phoneNumber),
              tmpAccessToken: tempAccessToken,
            }
          );

          slotQueryServiceId = suitableServiceId;
        }

        if (!isCurrentRequest) return;

        const clinicAvailability = await getClinicAvailability({
          clinicId,
          dates: getFilterDates(selectedDate),
          serviceId: slotQueryServiceId,
          timezone,
          timeBlocks: [0],
          cancelToken: source.token,
          ...(codeUrl && { code: codeUrl }),
          ...(serviceSlugNameUrl && { serviceSlugName: serviceSlugNameUrl }),
          ...(insuranceId && { insuranceId }),
          ...(familyBookingSessionId && { familyBookingSessionId }),
          ...(preferredDentistId && { dentistId: preferredDentistId }),
        });

        if (!isCurrentRequest) return;

        const availabilities = clinicAvailability?.doctors
          ? clinicAvailability.doctors
          : [];

        const adaptedSlots = availabilities.map((doctor) =>
          adaptAvailability({
            practitioner: {
              id: doctor.id,
              title: doctor.title,
              avatar: doctor.avatar || '',
              firstName: doctor.firstName,
              lastName: doctor.lastName,
              name: doctor.name,
              specialist: doctor.specialist,
            },
            availableBlocks: doctor.availableBlocks as ITimeSlot[],
            date: selectedDate,
            serviceId: requestServiceId,
            bookingServiceId: slotQueryServiceId,
            services,
            slotInterval,
          })
        );

        const resolved: IResolvedAvailability = {
          patientKey,
          date: selectedDate,
          bookingServiceId: slotQueryServiceId,
          slots: adaptedSlots,
        };

        setFetchedAvailability(resolved);
        batch(() => {
          dispatch(mergeSlotsPerDate({ [selectedDate]: adaptedSlots }));
          dispatch(cachePatientAvailability(resolved));
        });
      } catch (requestFailure) {
        if (axios.isCancel(requestFailure) || !isCurrentRequest) return;

        if (isFamilyBookingSessionExpired(requestFailure, phoneNumber)) {
          expireFamilyBookingSession({ dispatch, phoneNumber });

          return;
        }

        setFetchedAvailability(null);
        setFetchError(AVAILABILITY_ERROR_MESSAGE);
      } finally {
        if (isCurrentRequest) setIsFetchingAvailability(false);
      }
    };

    loadAvailability();

    return () => {
      isCurrentRequest = false;
      source.cancel();
    };
  }, [
    clinicId,
    codeUrl,
    currentPatient?.dob,
    currentPatient?.id,
    currentPatient?.preferredDentistId,
    currentPatient?.serviceId,
    dispatch,
    familyBookingSessionId,
    insuranceId,
    isCacheHit,
    isExamCleaningService,
    patientKey,
    phoneNumber,
    retryCount,
    selectedDate,
    serviceSlugNameUrl,
    services,
    slotInterval,
    tempAccessToken,
    timezone,
  ]);

  const updateDraftDate = useCallback(
    (date: string) => {
      const normalizedDate = moment(date).format(DATE_FORMAT);

      if (moment(normalizedDate).isBefore(moment(), 'day')) return;

      if (normalizedDate === selectedDate) return;

      navigatedPatientKeysRef.current.add(patientKey);

      dispatch(invalidatePatientAvailability(patientKey));

      setDraftDates((previousDates) => ({
        ...previousDates,
        [patientKey]: normalizedDate,
      }));
    },
    [dispatch, patientKey, selectedDate]
  );

  const getAuthOptions = (): IFamilyBookingAuthOptions => ({
    otpToken: loadOtpToken(phoneNumber),
    tmpAccessToken: tempAccessToken,
  });

  const setMutationIfMounted = (next: SlotMutation) => {
    if (!isUnmountedRef.current) setMutation(next);
  };

  const finishMutation = () => {
    inFlightIntentRef.current = null;
    setMutationIfMounted({ status: 'idle' });
  };

  const releaseHoldQuietly = (
    reservationId: string | null,
    authOptions: IFamilyBookingAuthOptions
  ) => {
    if (!reservationId) return;

    deleteFamilyBookingReservedSlotsAPI([reservationId], authOptions).catch(
      () => undefined
    );
  };

  const isSlotDisabled = (block: FamilyTimeSlotBlock) => {
    if (!currentPatient?.serviceId) return true;

    const candidateService = block.services.find(
      (service) => service.serviceId === currentPatient.serviceId
    );

    const candidateDuration =
      candidateService?.searchingService.duration ||
      getServiceDuration({
        serviceId: currentPatient.serviceId,
        bookingServiceId: candidateService?.bookingServiceId,
        childServiceId:
          currentPatient.childServiceId || currentPatient.serviceId,
        services,
      });

    return !candidateDuration;
  };

  const isSlotSelected = (doctorId: string, block: FamilyTimeSlotBlock) => {
    const blockStartTime = convertTimeHHmmss(block.value * MINUTE_OF_A_BLOCK);

    const isSameDoctor = currentPatient?.doctorId === doctorId;

    const isSameStartTime = currentPatient?.startTime === blockStartTime;

    const isSameDate = currentPatient?.appointmentDate === selectedDate;

    return isSameDoctor && isSameStartTime && isSameDate;
  };

  const findNextIncompletePatientIndex = (
    patientIndex: number,
    hasCascade: boolean
  ) =>
    selectedPatients.findIndex((patient, index) => {
      const isOtherPatient = index !== patientIndex;

      const isResetByCascade = hasCascade && index > patientIndex;

      return isOtherPatient && (!patient.isSlotSelected || isResetByCascade);
    });

  const releaseSlot = async (
    intent: ISlotIntent,
    authOptions: IFamilyBookingAuthOptions
  ) => {
    try {
      if (intent.previousReservationId) {
        await deleteFamilyBookingReservedSlotsAPI(
          [intent.previousReservationId],
          authOptions
        );
      }

      dispatch(resetSelectedPatientAtIndex(intent.patientIndex));
    } catch (requestFailure) {
      if (isFamilyBookingSessionExpired(requestFailure, phoneNumber)) {
        expireFamilyBookingSession({ dispatch, phoneNumber });

        return;
      }

      renderToast({ message: RELEASE_SLOT_ERROR_MESSAGE, type: 'error' });
    } finally {
      finishMutation();
    }
  };

  const reserveSlot = async (
    intent: ISlotIntent,
    sessionId: string,
    nextIncompletePatientIndex: number,
    authOptions: IFamilyBookingAuthOptions
  ) => {
    try {
      if (intent.patientId !== null) {
        const slotBookability = await checkFamilyBookingTimeBookabilityAPI(
          intent.patientId,
          {
            appointmentDate: intent.date,
            startTime: intent.startTime,
            clinicId,
            serviceId: intent.bookingServiceId,
          },
          authOptions
        );

        if (!isSlotTimeBookable(slotBookability)) {
          renderToast({ message: SLOT_NOT_BOOKABLE_MESSAGE, type: 'error' });

          return;
        }
      }

      setMutationIfMounted({ status: 'reserving', intent });

      const reservation = await reserveFamilyBookingSlotAPI(
        {
          serviceId: intent.bookingServiceId,
          clinicId,
          doctorId: intent.doctorId,
          appointmentDate: intent.date,
          startTime: intent.startTime,
          operatoryId: intent.operatoryId,
          sessionId,
        },
        authOptions
      );

      const reservationId = reservation?.patientFamilyBookingSessionId;

      if (isUnmountedRef.current) {
        releaseHoldQuietly(reservationId || null, authOptions);

        return;
      }

      const willAdvanceToNextPatient = nextIncompletePatientIndex !== -1;

      // Commit before releasing the previous hold: a failed release can then
      // only orphan the old reservation, which Redux still records.
      batch(() => {
        dispatch(
          updateSelectedPatientAtIndex({
            index: intent.patientIndex,
            patient: {
              appointmentDate: intent.date,
              startTime: intent.startTime,
              doctorId: intent.doctorId,
              practitionerName: intent.practitionerName,
              operatoryId: intent.operatoryId,
              dentist: intent.dentist,
              childServiceId: intent.childServiceId,
              chairOptimizations: intent.chairOptimizations,
              bookingServiceId: intent.bookingServiceId,
              ...(reservationId && {
                patientFamilyBookingSessionId: reservationId,
              }),
            },
          })
        );
        dispatch(updatedCurrentSelectedDate(intent.date));
      });

      releaseHoldQuietly(intent.previousReservationId, authOptions);

      if (!willAdvanceToNextPatient) return;

      setMutationIfMounted({ status: 'committed', intent });

      // Let the committed slot render as selected before the grid swaps to the
      // next patient; finishMutation only runs after this beat, so no new
      // interaction window opens.
      await new Promise((resolve) => setTimeout(resolve, SLOT_COMMIT_BEAT_MS));

      if (isUnmountedRef.current) return;

      dispatch(updatedCurrentSelectedPatientIndex(nextIncompletePatientIndex));
    } catch (requestFailure) {
      if (isFamilyBookingSessionExpired(requestFailure, phoneNumber)) {
        expireFamilyBookingSession({ dispatch, phoneNumber });

        return;
      }

      renderToast({
        message: getRequestErrorMessage(
          requestFailure,
          RESERVE_SLOT_ERROR_MESSAGE
        ),
        type: 'error',
      });
    } finally {
      finishMutation();
    }
  };

  const startSlotChange = (
    change: IPendingSlotChange,
    nextIncompletePatientIndex: number
  ) => {
    inFlightIntentRef.current = change.intent;

    if (change.kind === 'release') {
      setMutation({ status: 'releasing', intent: change.intent });
      releaseSlot(change.intent, change.authOptions);

      return;
    }

    setMutation({ status: 'checking', intent: change.intent });
    reserveSlot(
      change.intent,
      change.sessionId,
      nextIncompletePatientIndex,
      change.authOptions
    );
  };

  const onConfirmSlotChange = async () => {
    if (!pendingSlotChange || isReleasingDependents) return;

    const { intent, authOptions, dependentReservationIds } = pendingSlotChange;

    setIsReleasingDependents(true);

    try {
      if (dependentReservationIds.length) {
        await deleteFamilyBookingReservedSlotsAPI(
          dependentReservationIds,
          authOptions
        );
      }
    } catch (requestFailure) {
      if (isFamilyBookingSessionExpired(requestFailure, phoneNumber)) {
        expireFamilyBookingSession({ dispatch, phoneNumber });

        return;
      }

      renderToast({ message: RELEASE_SLOTS_ERROR_MESSAGE, type: 'error' });

      if (!isUnmountedRef.current) {
        setIsReleasingDependents(false);
        setPendingSlotChange(null);
      }

      return;
    }

    if (isUnmountedRef.current) return;

    batch(() => {
      setIsReleasingDependents(false);
      setPendingSlotChange(null);
      dispatch(resetSelectedPatientsFromIndex(intent.patientIndex));
      startSlotChange(
        pendingSlotChange,
        findNextIncompletePatientIndex(intent.patientIndex, true)
      );
    });
  };

  const onCancelSlotChange = () => {
    if (isReleasingDependents) return;

    setPendingSlotChange(null);
  };

  const onSelectSlot = (slot: FamilyTimeSlot, block: FamilyTimeSlotBlock) => {
    const isSlotChangeInProgress =
      !!inFlightIntentRef.current || !!pendingSlotChange;

    if (isSlotChangeInProgress || !currentPatient || isSlotDisabled(block)) {
      return;
    }

    const service = block.services.find(
      (item) => item.serviceId === currentPatient.serviceId
    );

    if (!service) return;

    const appointment = buildAppointmentFromSlot({
      slot,
      block,
      service,
      date: selectedDate,
      preferredDentistId: currentPatient.preferredDentistId || null,
      isExamCleaningService,
    });

    const intent: ISlotIntent = {
      patientIndex: currentSelectedPatientIndex,
      patientId: currentPatient.id || null,
      doctorId: appointment.doctorId,
      practitionerName: appointment.practitionerName,
      date: selectedDate,
      blockValue: block.value,
      startTime: appointment.startTime,
      bookingServiceId: service.bookingServiceId,
      childServiceId: appointment.childServiceId,
      operatoryId: appointment.operatoryId,
      dentist: appointment.dentist,
      chairOptimizations: appointment.chairOptimizations,
      previousReservationId:
        currentPatient.patientFamilyBookingSessionId || null,
    };

    const authOptions = getAuthOptions();
    const isDeselecting = isSlotSelected(slot.doctor.id, block);

    const dependentIndexes = selectedPatients.reduce<number[]>(
      (indexes, patient, index) =>
        index > intent.patientIndex && patient.isSlotSelected
          ? [...indexes, index]
          : indexes,
      []
    );

    const dependents = {
      dependentIndexes,
      dependentReservationIds: dependentIndexes
        .map((index) => selectedPatients[index].patientFamilyBookingSessionId)
        .filter((id): id is string => Boolean(id)),
    };

    const startOrConfirm = (change: IPendingSlotChange) => {
      if (dependentIndexes.length) {
        setPendingSlotChange(change);

        return;
      }

      startSlotChange(
        change,
        findNextIncompletePatientIndex(intent.patientIndex, false)
      );
    };

    if (isDeselecting) {
      startOrConfirm({ kind: 'release', intent, authOptions, ...dependents });

      return;
    }

    if (!familyBookingSessionId) {
      renderToast({ message: MISSING_SESSION_ERROR_MESSAGE, type: 'error' });

      return;
    }

    startOrConfirm({
      kind: 'reserve',
      intent,
      authOptions,
      sessionId: familyBookingSessionId,
      ...dependents,
    });
  };

  const onClickBack = () => {
    if (isMutatingSlot) return;

    if (selectedPatients.some((patient) => patient.isSlotSelected)) {
      setIsBackConfirmOpen(true);

      return;
    }

    dispatch(popFamilyStep());
  };

  const onConfirmBack = async () => {
    const reservationIds = selectedPatients
      .map((patient) => patient.patientFamilyBookingSessionId)
      .filter((id): id is string => Boolean(id));

    try {
      if (reservationIds.length) {
        await deleteFamilyBookingReservedSlotsAPI(
          reservationIds,
          getAuthOptions()
        );
      }

      batch(() => {
        dispatch(resetSelectedPatients());
        dispatch(popFamilyStep());
      });
    } catch (requestFailure) {
      if (isFamilyBookingSessionExpired(requestFailure, phoneNumber)) {
        expireFamilyBookingSession({ dispatch, phoneNumber });

        return;
      }

      renderToast({ message: RELEASE_SLOTS_ERROR_MESSAGE, type: 'error' });
      setIsBackConfirmOpen(false);
    }
  };

  const slots = sortPractitionerSlots({
    slots: availability?.slots || [],
    preferredPractitionerId: currentPatient?.preferredPractitionerId,
  });

  const isCurrentGrid = isAvailabilityCurrent({
    availability,
    patientKey,
    date: selectedDate,
  });

  const isSettled = !isLoadingAvailability && !availabilityError;

  const hasAnyOpenSlot = slots.some((slot) => slot.blocks.length > 0);

  const isAvailabilityEmpty = isCurrentGrid ? !hasAnyOpenSlot : !availability;

  const hasNoAvailability = isSettled && isAvailabilityEmpty;

  const hasGrid = isSettled && isCurrentGrid && hasAnyOpenSlot;

  const canAutoNavigateToNextAvailability =
    !navigatedPatientKeysRef.current.has(patientKey);

  const hasPatientWithoutSlot = selectedPatients.some(
    (patient) => !patient.isSlotSelected
  );

  const isContinueButtonDisabled = isMutatingSlot || hasPatientWithoutSlot;

  const slotChangePatientNames = (
    pendingSlotChange?.dependentIndexes || []
  ).map((index) =>
    concatFullName(
      selectedPatients[index]?.firstName,
      selectedPatients[index]?.lastName
    )
  );

  return (
    <div className={styles['time-selection']}>
      <PatientInfoSelection isLocked={isMutatingSlot} />

      <div className={styles['slot-view']}>
        {currentPatient && (
          <div className={styles['container']}>
            <section className={styles['section-header']}>
              <DayViewNavigation
                date={selectedDate}
                onDateUpdate={updateDraftDate}
                customInput={
                  <span className={styles['custom-input']}>
                    {moment(selectedDate).format('MMM DD')}
                  </span>
                }
                amount={1}
              />
            </section>

            <section className={styles['main-section']}>
              {isLoadingAvailability && (
                <div className={styles['state-container']}>
                  <AvailabilityProgress
                    message={LOADING_AVAILABILITY_MESSAGE}
                  />
                </div>
              )}

              {!isLoadingAvailability && availabilityError && (
                <div className={styles['state-container']}>
                  <span>{availabilityError}</span>

                  <button
                    type="button"
                    className={styles['retry-button']}
                    onClick={() => setRetryCount((count) => count + 1)}
                  >
                    Retry
                  </button>
                </div>
              )}

              {hasNoAvailability && (
                <div className={styles['state-container']}>
                  <NextAvailability
                    key={`${patientKey}|${selectedDate}`}
                    selectedDate={selectedDate}
                    canAutoNavigate={canAutoNavigateToNextAvailability}
                    onDateUpdate={updateDraftDate}
                  />
                </div>
              )}

              {hasGrid && (
                <SlotGrid
                  contentKey={`${patientKey}|${selectedDate}`}
                  slots={slots}
                  mutation={mutation}
                  preferredPractitionerId={
                    currentPatient.preferredPractitionerId
                  }
                  isSlotSelected={isSlotSelected}
                  isSlotDisabled={isSlotDisabled}
                  onSelectSlot={onSelectSlot}
                />
              )}
            </section>

            <NavigationController
              isDisabledSubmit={isContinueButtonDisabled}
              title="Next"
              isTimeSelection
              onClickBack={onClickBack}
              onClickNext={() => dispatch(setReviewOpen(true))}
            />

            <BackConfirmationModal
              open={isBackConfirmOpen}
              onCancel={() => setIsBackConfirmOpen(false)}
              onConfirm={onConfirmBack}
            />

            <SlotChangeConfirmationModal
              open={!!pendingSlotChange}
              patientNames={slotChangePatientNames}
              isReleasing={isReleasingDependents}
              onCancel={onCancelSlotChange}
              onConfirm={onConfirmSlotChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SlotSelection;
